import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Hand, TrendingUp, ClipboardList, FileText, Users, CheckCircle, Star, ArrowRight, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import HeroCarousel from '../components/HeroCarousel';
import { clinicInfo, whyMindMotion, conditions, howItWorks, testimonials, clinicImages } from '../data/mockData';

const HomePage = () => {
  const iconMap = {
    Award,
    Hand,
    TrendingUp,
    ClipboardList,
    FileText,
    Users,
    CheckCircle
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${clinicInfo.whatsapp.replace(/[^0-9]/g, '')}?text=Hi, I would like to book an appointment`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${clinicInfo.phone}`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Carousel Section */}
      <HeroCarousel />

      {/* Why Mind Motion Section - 2 Column Layout */}
      <section className="py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Why Mind Motion?
            </h2>
            <p className="text-lg text-muted-foreground">
              Evidence-based physiotherapy that delivers real results
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Left Column - Features */}
            <div className="space-y-6">
              {whyMindMotion.map((item) => {
                const Icon = iconMap[item.icon];
                return (
                  <div key={item.id} className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-14 h-14 bg-secondary rounded-full flex items-center justify-center">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-2">{item.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right Column - Real Clinic Images */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <img 
                  src={clinicImages.facility} 
                  alt="Mind Motion Clinic Facility"
                  className="rounded-lg shadow-lg w-full h-64 object-cover"
                />
              </div>
              <img 
                src={clinicImages.treatmentRoom1} 
                alt="Treatment Room"
                className="rounded-lg shadow-lg w-full h-48 object-cover"
              />
              <img 
                src={clinicImages.equipment} 
                alt="Professional Equipment"
                className="rounded-lg shadow-lg w-full h-48 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Conditions We Help With */}
      <section className="py-12 md:py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Conditions We Help With
            </h2>
            <p className="text-lg text-muted-foreground">
              Specialized treatment for a wide range of musculoskeletal conditions
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {conditions.slice(0, 6).map((condition) => (
              <Link 
                key={condition.id} 
                to={`/treatment/${condition.id}`}
                className="group"
              >
                <Card className="overflow-hidden border-2 border-gray-100 hover:border-primary transition-all duration-300 hover:shadow-lg h-full">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={condition.image} 
                      alt={condition.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-primary mb-2 group-hover:text-primary/80 transition-colors">
                      {condition.name}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4">{condition.shortDesc}</p>
                    <span className="text-primary font-medium text-sm inline-flex items-center">
                      Learn more <ArrowRight className="ml-1 w-4 h-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/treatments">
              <Button 
                size="lg"
                className="px-8 transition-all duration-300"
              >
                View All Treatments
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How We Help You */}
      <section className="py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              How We Help You
            </h2>
            <p className="text-lg text-muted-foreground">
              Go from pain to recovery in 4 simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => {
              const Icon = iconMap[step.icon];
              return (
                <div key={step.step} className="relative text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-secondary rounded-full mb-6 relative">
                    <Icon className="w-10 h-10 text-primary" />
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {step.step}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-3">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                  {index < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-secondary" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 md:py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              What Our Patients Say
            </h2>
            <p className="text-lg text-muted-foreground">
              Real stories from real people who found relief at Mind Motion
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-gray-100 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#FFB800] text-[#FFB800]" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4 leading-relaxed italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-bold text-primary">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.condition}</p>
                    <p className="text-xs text-muted-foreground/70 mt-1 flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {testimonial.location}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Start Your Recovery Journey
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Book your comprehensive assessment today and take the first step towards pain-free movement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleWhatsApp}
              size="lg"
              className="bg-primary hover:bg-[hsl(var(--facebook-blue-hover))] text-white text-lg px-8 py-6 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book on WhatsApp
            </Button>
            <Button 
              onClick={handleCall}
              size="lg"
              variant="outline"
              className="bg-white text-primary hover:bg-gray-100 border-2 border-white text-lg px-8 py-6 transition-all duration-300"
            >
              Call {clinicInfo.phone}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
